import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { format, parseISO, subDays, eachDayOfInterval } from 'date-fns';
import {
  Link2,
  MousePointerClick,
  Activity,
  Users,
  Globe,
  Smartphone,
  TrendingUp,
  UserPlus,
  ArrowRight,
} from 'lucide-react';
import { apiFetch } from '@/client/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/client/components/ui/card';
import { Skeleton } from '@/client/components/ui/skeleton';
import { Badge } from '@/client/components/ui/badge';
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
} from '@/client/components/ui/chart';

interface DashboardData {
  overview: {
    totalLinks: number;
    totalClicks: number;
    activeLinks: number;
    activeUsers: number;
    totalUsers: number;
    publicLinks: number;
    userLinks: number;
    pendingRequests: number;
    activeLinkRate: number;
  };
  trends: {
    clicksByDay: Array<{ date: string; clicks: number }>;
    linksByDay: Array<{ date: string; count: number }>;
    usersByDay: Array<{ date: string; count: number }>;
  };
  distribution: {
    countries: Record<string, number>;
    devices: Record<string, number>;
    referrers: Record<string, number>;
  };
}

// Chart configurations using shadcn/ui chart system
const clicksChartConfig = {
  clicks: {
    label: 'Clicks',
    color: 'var(--chart-1)',
  },
} satisfies ChartConfig;

const linksChartConfig = {
  count: {
    label: 'Links Created',
    color: 'var(--chart-1)',
  },
} satisfies ChartConfig;

// Pie chart colors using CSS variables
const pieChartColors = [
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
  'var(--chart-4)',
  'var(--chart-5)',
  'var(--chart-1)',
  'var(--chart-2)',
  'var(--chart-3)',
];

export function AdminDashboardPage() {
  const { data, isLoading, error } = useQuery<DashboardData>({
    queryKey: ['admin', 'dashboard'],
    queryFn: async () => {
      const response = await apiFetch('/api/admin/dashboard');
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Dashboard API error:', response.status, errorText);
        throw new Error(`Failed to fetch dashboard: ${response.status}`);
      }
      return response.json();
    },
    refetchInterval: 60000,
  });

  // Fill in missing days for charts
  const clicksChartData = useMemo(() => {
    if (!data?.trends.clicksByDay) return [];

    const endDate = new Date();
    const startDate = subDays(endDate, 29);
    const dayMap = new Map<string, number>();

    eachDayOfInterval({ start: startDate, end: endDate }).forEach((date) => {
      const key = format(date, 'yyyy-MM-dd');
      dayMap.set(key, 0);
    });

    data.trends.clicksByDay.forEach((d) => {
      if (dayMap.has(d.date)) {
        dayMap.set(d.date, d.clicks);
      }
    });

    return Array.from(dayMap.entries()).map(([date, clicks]) => ({
      date,
      displayDate: format(parseISO(date), 'MMM d'),
      clicks,
    }));
  }, [data?.trends.clicksByDay]);

  const linksChartData = useMemo(() => {
    if (!data?.trends.linksByDay) return [];

    const endDate = new Date();
    const startDate = subDays(endDate, 29);
    const dayMap = new Map<string, number>();

    eachDayOfInterval({ start: startDate, end: endDate }).forEach((date) => {
      const key = format(date, 'yyyy-MM-dd');
      dayMap.set(key, 0);
    });

    data.trends.linksByDay.forEach((d) => {
      if (dayMap.has(d.date)) {
        dayMap.set(d.date, d.count);
      }
    });

    return Array.from(dayMap.entries()).map(([date, count]) => ({
      date,
      displayDate: format(parseISO(date), 'MMM d'),
      count,
    }));
  }, [data?.trends.linksByDay]);

  const countriesChartData = useMemo(() => {
    if (!data?.distribution.countries) return { data: [], config: {} as ChartConfig };

    const sorted = Object.entries(data.distribution.countries)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    let items: Array<{ name: string; count: number; fill: string }>;
    if (sorted.length > 7) {
      const top = sorted.slice(0, 6);
      const otherCount = sorted.slice(6).reduce((sum, item) => sum + item.count, 0);
      items = [...top, { name: 'Other', count: otherCount }].map((item, index) => ({
        ...item,
        fill: pieChartColors[index % pieChartColors.length] || 'var(--chart-1)',
      }));
    } else {
      items = sorted.map((item, index) => ({
        ...item,
        fill: pieChartColors[index % pieChartColors.length] || 'var(--chart-1)',
      }));
    }

    // Build chart config for legend/tooltips
    const config: ChartConfig = {};
    items.forEach((item, index) => {
      config[item.name] = {
        label: item.name,
        color: pieChartColors[index % pieChartColors.length],
      };
    });

    return { data: items, config };
  }, [data?.distribution.countries]);

  const devicesData = useMemo(() => {
    if (!data?.distribution.devices) return [];

    return Object.entries(data.distribution.devices)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [data?.distribution.devices]);

  const totalDeviceClicks = useMemo(() => {
    return devicesData.reduce((sum, d) => sum + d.count, 0);
  }, [devicesData]);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-28 rounded-2xl" />
          ))}
        </div>
        <Skeleton className="h-72 rounded-2xl" />
        <div className="grid gap-6 md:grid-cols-2">
          <Skeleton className="h-64 rounded-2xl" />
          <Skeleton className="h-64 rounded-2xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="rounded-2xl border-destructive/50 bg-destructive/10 py-0">
        <CardContent className="p-6">
          <h2 className="text-lg font-semibold text-destructive">Error Loading Dashboard</h2>
          <p className="text-muted-foreground mt-2">{error.message}</p>
          <p className="text-sm text-muted-foreground mt-4">
            Check the browser console for more details.
          </p>
        </CardContent>
      </Card>
    );
  }

  const overview = data?.overview ?? {
    totalLinks: 0,
    totalClicks: 0,
    activeLinks: 0,
    activeUsers: 0,
    totalUsers: 0,
    publicLinks: 0,
    userLinks: 0,
    pendingRequests: 0,
    activeLinkRate: 0,
  };

  return (
    <div className="space-y-6 stagger-children">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">Overview of your URL shortening service.</p>
      </div>

      {/* Overview Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard
          title="Total Links"
          value={overview.totalLinks.toLocaleString()}
          icon={<Link2 className="size-5" />}
          subtitle={`${overview.publicLinks} public, ${overview.userLinks} user`}
        />
        <StatCard
          title="Total Clicks"
          value={overview.totalClicks.toLocaleString()}
          icon={<MousePointerClick className="size-5" />}
          subtitle="All time"
        />
        <StatCard
          title="Active Links"
          value={overview.activeLinks.toLocaleString()}
          icon={<Activity className="size-5" />}
          subtitle={`${overview.activeLinkRate}% of total`}
          highlight={overview.activeLinkRate > 50}
        />
        <StatCard
          title="Users"
          value={overview.totalUsers.toLocaleString()}
          icon={<Users className="size-5" />}
          subtitle={`${overview.activeUsers} active`}
        />
      </div>

      {/* Pending Requests Alert */}
      {overview.pendingRequests > 0 && (
        <Card className="rounded-2xl border-primary/30 bg-primary/5 hover:bg-primary/10 transition-colors py-0">
          <Link to="/admin/access-requests" className="flex items-center justify-between p-4">
            <div className="flex items-center gap-3">
              <div className="size-10 rounded-xl bg-primary/20 flex items-center justify-center">
                <UserPlus className="size-5 text-primary" />
              </div>
              <div>
                <p className="font-medium flex items-center gap-2">
                  {overview.pendingRequests} pending access request
                  {overview.pendingRequests > 1 ? 's' : ''}
                  <Badge variant="primary-subtle">{overview.pendingRequests}</Badge>
                </p>
                <p className="text-sm text-muted-foreground">Review and approve new users</p>
              </div>
            </div>
            <ArrowRight className="size-5 text-muted-foreground" />
          </Link>
        </Card>
      )}

      {/* Click Trend Chart */}
      <Card className="rounded-2xl py-0">
        <CardHeader className="flex-row items-center gap-2 pb-0">
          <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <TrendingUp className="size-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-lg">Clicks Over Time</CardTitle>
            <p className="text-sm text-muted-foreground">Last 30 days</p>
          </div>
        </CardHeader>
        <CardContent className="py-6">
          {clicksChartData.length > 0 ? (
            <ChartContainer config={clicksChartConfig} className="h-64 w-full">
              <AreaChart data={clicksChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="var(--color-clicks)" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="var(--color-clicks)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="displayDate"
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                  fontSize={12}
                />
                <YAxis tickLine={false} axisLine={false} allowDecimals={false} fontSize={12} />
                <ChartTooltip
                  content={
                    <ChartTooltipContent
                      labelFormatter={(value) => value}
                      formatter={(value) => (
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">Clicks</span>
                          <span className="font-mono font-medium tabular-nums ml-auto">
                            {value?.toLocaleString()}
                          </span>
                        </div>
                      )}
                    />
                  }
                />
                <Area
                  type="monotone"
                  dataKey="clicks"
                  stroke="var(--color-clicks)"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorClicks)"
                />
              </AreaChart>
            </ChartContainer>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No click data available yet
            </div>
          )}
        </CardContent>
      </Card>

      {/* Second Row: Link Creation and Geographic */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Link Creation Trend */}
        <Card className="rounded-2xl py-0">
          <CardHeader className="flex-row items-center gap-2 pb-0">
            <div className="size-10 rounded-xl bg-accent/10 flex items-center justify-center">
              <Link2 className="size-5 text-accent" />
            </div>
            <CardTitle className="text-lg">Link Creation</CardTitle>
          </CardHeader>
          <CardContent className="py-6">
            {linksChartData.length > 0 && linksChartData.some((d) => d.count > 0) ? (
              <ChartContainer config={linksChartConfig} className="h-56 w-full">
                <BarChart data={linksChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="displayDate"
                    tickLine={false}
                    axisLine={false}
                    interval="preserveStartEnd"
                    fontSize={11}
                  />
                  <YAxis tickLine={false} axisLine={false} allowDecimals={false} fontSize={11} />
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        labelFormatter={(value) => value}
                        formatter={(value) => (
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">Links Created</span>
                            <span className="font-mono font-medium tabular-nums ml-auto">
                              {value?.toLocaleString()}
                            </span>
                          </div>
                        )}
                      />
                    }
                  />
                  <Bar dataKey="count" fill="var(--color-count)" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartContainer>
            ) : (
              <div className="h-56 flex items-center justify-center text-muted-foreground">
                No link creation data yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Geographic Distribution */}
        <Card className="rounded-2xl py-0">
          <CardHeader className="flex-row items-center gap-2 pb-0">
            <div className="size-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Globe className="size-5 text-blue-500" />
            </div>
            <CardTitle className="text-lg">Top Countries</CardTitle>
          </CardHeader>
          <CardContent className="py-6">
            {countriesChartData.data.length > 0 ? (
              <ChartContainer config={countriesChartData.config} className="h-56 w-full">
                <PieChart>
                  <Pie
                    data={countriesChartData.data}
                    cx="50%"
                    cy="50%"
                    innerRadius={45}
                    outerRadius={75}
                    paddingAngle={2}
                    dataKey="count"
                    nameKey="name"
                  >
                    {countriesChartData.data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Pie>
                  <ChartTooltip
                    content={
                      <ChartTooltipContent
                        nameKey="name"
                        formatter={(value, _name, props) => (
                          <div className="flex items-center gap-2">
                            <span className="text-muted-foreground">{props.payload?.name}</span>
                            <span className="font-mono font-medium tabular-nums ml-auto">
                              {value?.toLocaleString()} clicks
                            </span>
                          </div>
                        )}
                      />
                    }
                  />
                  <ChartLegend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    content={<ChartLegendContent nameKey="name" />}
                  />
                </PieChart>
              </ChartContainer>
            ) : (
              <div className="h-56 flex items-center justify-center text-muted-foreground">
                No geographic data yet
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Third Row: Devices and Health */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Device Breakdown */}
        <Card className="rounded-2xl py-0">
          <CardHeader className="flex-row items-center gap-2 pb-0">
            <div className="size-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <Smartphone className="size-5 text-purple-500" />
            </div>
            <CardTitle className="text-lg">Device Types</CardTitle>
          </CardHeader>
          <CardContent className="py-6">
            {devicesData.length > 0 ? (
              <div className="space-y-4">
                {devicesData.map((item) => {
                  const percentage =
                    totalDeviceClicks > 0 ? Math.round((item.count / totalDeviceClicks) * 100) : 0;
                  return (
                    <div key={item.name}>
                      <div className="flex justify-between text-sm mb-1.5">
                        <span className="font-medium">{item.name}</span>
                        <span className="text-muted-foreground">
                          {item.count.toLocaleString()} ({percentage}%)
                        </span>
                      </div>
                      <div className="h-2.5 bg-muted rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all bg-primary"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="h-32 flex items-center justify-center text-muted-foreground">
                No device data yet
              </div>
            )}
          </CardContent>
        </Card>

        {/* Health Indicators */}
        <Card className="rounded-2xl py-0">
          <CardHeader className="flex-row items-center gap-2 pb-0">
            <div className="size-10 rounded-xl bg-success/10 flex items-center justify-center">
              <Activity className="size-5 text-success" />
            </div>
            <CardTitle className="text-lg">Health Indicators</CardTitle>
          </CardHeader>
          <CardContent className="py-6 space-y-3">
            <div className="flex justify-between items-center p-3 rounded-xl bg-muted/50">
              <span className="text-sm">Active Link Rate</span>
              <Badge
                variant={
                  overview.activeLinkRate > 50
                    ? 'success'
                    : overview.activeLinkRate > 20
                      ? 'default'
                      : 'destructive'
                }
              >
                {overview.activeLinkRate}%
              </Badge>
            </div>
            <div className="flex justify-between items-center p-3 rounded-xl bg-muted/50">
              <span className="text-sm">Public vs User Links</span>
              <span className="font-medium">
                {overview.publicLinks} / {overview.userLinks}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 rounded-xl bg-muted/50">
              <span className="text-sm">Total Users</span>
              <span className="font-medium">{overview.totalUsers}</span>
            </div>
            <div className="flex justify-between items-center p-3 rounded-xl bg-muted/50">
              <span className="text-sm">Active Users (with links)</span>
              <span className="font-medium">{overview.activeUsers}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string;
  icon: React.ReactNode;
  subtitle?: string;
  highlight?: boolean;
}

function StatCard({ title, value, icon, subtitle, highlight }: StatCardProps) {
  return (
    <Card
      className={`rounded-2xl group relative overflow-hidden py-0 ${highlight ? 'ring-1 ring-success/30' : ''}`}
    >
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
      <CardContent className="relative p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-muted-foreground">{title}</span>
          <div className="size-10 rounded-xl bg-muted flex items-center justify-center text-muted-foreground group-hover:scale-110 transition-transform">
            {icon}
          </div>
        </div>
        <div className="text-3xl font-bold tracking-tight">{value}</div>
        {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
      </CardContent>
    </Card>
  );
}
